/* ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 ~ Copyright 2020 Adobe Systems Incorporated
 ~
 ~ Licensed under the Apache License, Version 2.0 (the 'License');
 ~ you may not use this file except in compliance with the License.
 ~ You may obtain a copy of the License at
 ~
 ~     http://www.apache.org/licenses/LICENSE-2.0
 ~
 ~ Unless required by applicable law or agreed to in writing, software
 ~ distributed under the License is distributed on an 'AS IS' BASIS,
 ~ WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 ~ See the License for the specific language governing permissions and
 ~ limitations under the License.
 ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~ */
import React from 'react';

import { Utils } from '@adobe/aem-react-editable-components';
import PropTypes from 'prop-types';
import { Route, Switch, withRouter } from 'react-router';

import { PrivateRoute } from 'components/PrivateRouteComponent/PrivateRoute';

import { PUBLIC_AEM_PAGE_TEMPLATES } from 'global/constants';

/**
 * Helper that facilitate the use of the {@link Route} component
 */

/**
 * Returns a composite component where a {@link Route} component wraps the provided component
 *
 * @param {React.Component} WrappedComponent    - React component to be wrapped
 * @param {string} [extension=html]             - extension used to identify a route amongst the tree of resource URLs
 * @returns {CompositeRoute}
 */

export const withRoute = (WrappedComponent, extensionParam) => {
  const CompositeRoute = (props) => {
    const { cqPath, templateName } = props;
    const isPublicRoute = PUBLIC_AEM_PAGE_TEMPLATES.includes(templateName);

    if (!cqPath) {
      return <WrappedComponent {...props} />;
    }

    const resolvedExtension = extensionParam || 'html';

    // Context path + route path + extension
    const constructedRoute = `${cqPath}.${resolvedExtension}`;
    const isEditor = Utils.isInEditor();

    if (isEditor) {
      return (
        <Route
          key={cqPath}
          exact
          path={constructedRoute}
          render={(routeProps) => <WrappedComponent {...props} {...routeProps} />}
        />
      );
    }

    if (isPublicRoute) {
      return (
        <Switch>
          <Route
            key={cqPath}
            exact
            path={constructedRoute}
            render={(routeProps) => <WrappedComponent {...props} {...routeProps} />}
          />
        </Switch>
      );
    }

    return (
      <PrivateRoute
        key={cqPath}
        exact
        path={constructedRoute}
        render={(routeProps) => <WrappedComponent {...props} {...routeProps} />}
      />
    );
  };
  CompositeRoute.propTypes = {
    cqPath: PropTypes.string.isRequired,
    templateName: PropTypes.string.isRequired,
  };
  return withRouter(CompositeRoute);
};
